import React, { useState } from 'react';
import {
  Typography,
  Grid,
  Stack,
  Dialog,
  DialogContent,
  CardMedia,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import StyledContentBox from 'config/material-ui/styled/ContentBox.styled';
import EditIcon from 'assets/icons/EditIcon';
import { useAppDispatch } from 'store/hooks';
import { setErrorCatch, setSuccessStatus } from 'store/status/actions';
import { useUpdateProjectMutation } from 'store/projects/projects';
import NotesUrl from 'assets/img/notes.svg';
import NotesForm from 'common/NotesForm/NotesForm';
import { FormPayload } from 'common/NotesForm/interfaces';
import useEditProjectFormContext from 'components/EditProjectForm/context/useEditProjectFormContext';
import TextButton from 'common/TextButton/TextButton';
import DialogHeader from 'common/DialogHeader/DialogHeader';

export default function OverviewNotes() {
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const { project } = useEditProjectFormContext();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [updateProject, { isLoading }] = useUpdateProjectMutation();

  const handleUpdateProjectNotes = async (data: FormPayload) => {
    try {
      await updateProject({
        id: project.id,
        notes: data.note,
      }).unwrap();
      dispatch(setSuccessStatus(true));
      setIsNotesModalOpen(false);
    } catch (error) {
      dispatch(setErrorCatch(error));
    }
  };

  return (
    <>
      <Grid item xs={4}>
        <StyledContentBox py={3} px={3}>
          <Stack spacing={3}>
            <Typography variant="h6">{t('projects.notes')}</Typography>
            {project?.notes ? (
              <Stack alignItems="flex-start" spacing={3}>
                <Typography variant="inputValue" color="text.secondary">
                  {project.notes}
                </Typography>
                <TextButton
                  onClick={() => setIsNotesModalOpen(true)}
                  label="projects.edit_notes"
                  color="icon.main"
                  icon={
                    <EditIcon
                      sx={{ color: 'primary.main', fontSize: '2rem' }}
                    />
                  }
                />
              </Stack>
            ) : (
              <Stack alignItems="center" width="100%">
                <CardMedia
                  component="img"
                  alt="notes"
                  image={NotesUrl}
                  sx={{ width: '12.8rem', height: '12.8rem' }}
                />
                <Typography mb={2} color="text.secondary">
                  {t('projects.no_notes')}
                </Typography>
                <TextButton
                  onClick={() => setIsNotesModalOpen(true)}
                  label="projects.add_notes"
                  color="icon.main"
                />
              </Stack>
            )}
          </Stack>
        </StyledContentBox>
      </Grid>
      <Dialog
        open={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        fullWidth
      >
        <DialogHeader
          title={t('projects.edit_notes')}
          onClose={() => setIsNotesModalOpen(false)}
        />
        <DialogContent>
          <NotesForm
            initValues={{ note: project?.notes || '' }}
            onSubmit={handleUpdateProjectNotes}
            isSubmitting={isLoading}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
